"use client";

import { SwitchVerticalIcon } from "@heroicons/react/outline";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@radix-ui/react-dropdown-menu";
import { Check, CircleDot, ListFilter } from "lucide-react";
import * as React from "react";
import { ReactNode, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "./badge";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Separator } from "./separator";

export interface Option {
  value: any;
  label: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

function mapOptionValuesToLabels(options: Option[]) {
  // return new Map(options.map((option) => [option.value, option.label]))
  return options.reduce<Record<string, string>>((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});
}

function getComboboxDisplayValue(value: string[], options: Option[], limit = 2) {
  const optionsMap = mapOptionValuesToLabels(options);
  const filteredLabels = value.map((val) => optionsMap[val]);
  const visibleLabels = filteredLabels.slice(0, limit);
  const stringifiedLabels = visibleLabels.join(", ");
  const diff = filteredLabels.length - visibleLabels.length;
  if (diff > 0) {
    return [stringifiedLabels, diff].join(", + ");
  }
  return stringifiedLabels;
}

interface ComboboxProps {
  value: string[];
  options: Option[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  noResults?: string;
  buttonContent?: ReactNode;
  minWidth?: number;
  clearable?: boolean;
  searchable?: boolean;
}

export function Combobox(props: ComboboxProps) {
  const [open, setOpen] = React.useState(false);

  const { value, onChange, options, buttonContent } = props;

  const displayValue = getComboboxDisplayValue(props.value, options);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          type="button"
          aria-expanded={open}
          className="flex-nowrap justify-between"
          style={{
            minWidth: typeof props.minWidth === "number" ? `${props.minWidth}px` : undefined,
          }}
        >
          {buttonContent ? buttonContent : value.length > 0 ? displayValue : props.placeholder}
          <SwitchVerticalIcon className="shrink-0 w-4 h-4 ml-2 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty className="p-4 text-sm">Nothing found matching that query.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    onChange(
                      value.includes(option.value)
                        ? value.filter((v) => v !== option.value)
                        : [...value, option.value],
                    );
                    setOpen(false);
                  }}
                  className="text-sm"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value.includes(option.value) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function DropdownMenuTableFilter(
  props: ComboboxProps & {
    title?: string;
    type: "radio" | "checkbox";
  },
) {
  const { type = "checkbox", title, value, options } = props;
  const selectedValues = new Set(value);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{props.title}</DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="p-0">
        <Command>
          <CommandInput className="text-sm" placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        if (type === "radio") {
                          selectedValues.clear();
                        }
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      props.onChange(filterValues);
                    }}
                    className="text-sm"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center border border-foreground",
                        type === "radio" ? "rounded-full" : "rounded-sm",
                        isSelected
                          ? "bg-foreground text-background"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      {type === "radio" ? (
                        <CircleDot className={cn("h-4 w-4")} />
                      ) : (
                        <Check className={cn("h-4 w-4")} />
                      )}
                    </div>
                    {option.icon && <option.icon className="text-muted-foreground w-4 h-4 mr-2" />}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => props.onChange([])}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

export function TableFilter(
  props: ComboboxProps & {
    title?: string;
    type?: "radio" | "checkbox";
    contentClassname?: string;
  },
) {
  const { type = "checkbox", title, value, options, clearable = true, searchable = true } = props;
  const selectedValues = new Set(value);

  function handleRadioChange(option: Option, isSelected: boolean) {
    selectedValues.clear();
    if (isSelected) {
      if (!clearable) {
        selectedValues.add(option.value);
      }
    } else {
      selectedValues.add(option.value);
    }
    props.onChange(Array.from(selectedValues));
  }

  function handleCheckboxChange(option: Option, isSelected: boolean) {
    if (isSelected) {
      selectedValues.delete(option.value);
    } else {
      selectedValues.add(option.value);
    }
    props.onChange(Array.from(selectedValues));
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" type="button" size="sm" className={cn("border-dashed")}>
          <ListFilter className={cn("w-4 h-4 mr-2", {})} />
          <span className={cn("text-sm")}>{title}</span>
          {selectedValues.size > 0 && (
            <>
              <Separator orientation="vertical" className="h-4 mx-2" />
              <Badge
                variant="secondary"
                className={cn(
                  "px-1 justify-center min-w-[22px] min-h-[22px] font-normal rounded-sm",
                  {
                    "flex xl:hidden": type !== "radio",
                    hidden: type === "radio",
                  },
                )}
              >
                {selectedValues.size}
              </Badge>
              <div
                className={cn("space-x-1", {
                  "hidden xl:flex": type !== "radio",
                  flex: type === "radio",
                })}
              >
                {selectedValues.size > 2 ? (
                  <Badge variant="secondary" className="px-1 font-normal rounded-sm">
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="whitespace-nowrap px-1 font-normal rounded-sm"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-[200px] p-0", props.contentClassname)} align="start">
        <Command>
          {searchable ? (
            <CommandInput className="text-sm" placeholder={title} data-1p-ignore />
          ) : null}
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (type === "radio") {
                        handleRadioChange(option, isSelected);
                        return;
                      }
                      handleCheckboxChange(option, isSelected);
                    }}
                    className="text-sm"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center border border-foreground",
                        type === "radio" ? "rounded-full" : "rounded-sm",
                        isSelected
                          ? "bg-foreground text-background"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      {type === "radio" ? (
                        <CircleDot className={cn("h-4 w-4")} />
                      ) : (
                        <Check className={cn("h-4 w-4")} />
                      )}
                    </div>
                    {option.icon && <option.icon className="w-4 h-4 mr-2" />}
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      {option.description ? (
                        <span className="text-zinc-300 text-sm">{option.description}</span>
                      ) : null}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {clearable && selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => props.onChange([])}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function TableSort(
  props: ComboboxProps & {
    title?: string;
    className?: string;
    buttonSize?: "sm" | "lg";
  },
) {
  const { title, value, options, buttonSize = "sm" } = props;
  const selectedValues = new Set(value);

  const maybeCurrentValue = useMemo(
    () => options.find((option) => value?.[0] === option.value),
    [options, value],
  );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          size={buttonSize}
          className={cn("whitespace-nowrap border-dashed", props.className)}
        >
          <SwitchVerticalIcon
            className={cn("w-4 h-4", selectedValues.size > 0 ? "mr-2" : "sm:mr-2")}
          />
          <span className="text-sm">{title}</span>
          {selectedValues.size > 0 && maybeCurrentValue && (
            <>
              <Separator orientation="vertical" className="h-4 mx-2" />
              <Badge variant="secondary" className="px-1 font-normal rounded-sm">
                {maybeCurrentValue.label}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="end">
        <Command>
          <CommandInput className="text-sm" placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.clear();
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      props.onChange(filterValues);
                    }}
                    className="text-sm"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center border border-foreground rounded-full",
                        isSelected
                          ? "bg-foreground text-background"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <CircleDot className={cn("h-4 w-4")} />
                    </div>
                    {option.icon && <option.icon className="text-muted-foreground w-4 h-4 mr-2" />}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => props.onChange([])}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
